import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import ChallengeEntry from "../models/ChallengeEntry";
import Workout from "../models/Workout";
import WorkoutSession from "../models/WorkoutSession";
import Goal from "../models/Goal";
import GoalAttempt from "../models/GoalAttempt";

const SkeletonCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden animate-pulse">
    <div className="h-40 bg-gray-200 dark:bg-gray-700" />
    <div className="p-5">
      <div className="flex justify-between items-start mb-3">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32" />
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
      </div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4" />
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" />
      </div>
      <div className="flex justify-between mb-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
      </div>
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full" />
    </div>
  </div>
);

const WeeklyTracker = ({ weekData, weekDates, totalGoal, onDayClick }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayIndex = (new Date().getDay() + 6) % 7; // Convert Sunday=0 to Monday=0 format

  return (
    <div className="flex justify-between items-end gap-1 sm:gap-2">
      {days.map((day, index) => {
        const dayData = weekData[index] || { total: 0 };
        const isToday = index === todayIndex;
        const hasProgress = dayData.total > 0;
        const percentage = totalGoal > 0 ? Math.min((dayData.total / totalGoal) * 100, 100) : 0;
        const dateStr = weekDates[index];

        return (
          <button
            key={day}
            onClick={() => onDayClick(dateStr)}
            className="flex flex-col items-center gap-2 group"
          >
            <span className={`text-xs font-medium ${isToday ? 'text-white' : 'text-white/70'}`}>
              {day}
            </span>
            <div className={`relative w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all group-hover:scale-110 ${
              isToday
                ? 'bg-white/30 ring-2 ring-white ring-offset-1 sm:ring-offset-2 ring-offset-indigo-500'
                : hasProgress
                  ? 'bg-green-100 dark:bg-green-900'
                  : 'bg-white/10'
            }`}>
              {/* Progress ring */}
              <svg className="absolute w-8 h-8 sm:w-12 sm:h-12 -rotate-90" viewBox="0 0 48 48">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-white/30"
                />
                {hasProgress && (
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${percentage * 1.256} 125.6`}
                    className="text-green-500 dark:text-green-400"
                  />
                )}
              </svg>
              {/* Checkmark or number */}
              {hasProgress ? (
                <svg className="w-3 h-3 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-white/50 text-[10px] sm:text-sm z-10">-</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const StartPage = () => {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [weekDates, setWeekDates] = useState([]);
  const [totalGoal, setTotalGoal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [workouts, setWorkouts] = useState([]);
  const [workoutStats, setWorkoutStats] = useState({});
  const [goals, setGoals] = useState([]);
  const [goalStats, setGoalStats] = useState({});
  const navigate = useNavigate();

  const handleAddNewEntry = () => {
    navigate("/register");
  };

  // Get the dates for the current week (Monday to Sunday)
  const getWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    // Adjust so Monday = 0, Sunday = 6
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const weekDates = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + mondayOffset + i);
      weekDates.push(date.toISOString().split('T')[0]);
    }
    return weekDates;
  };

  useEffect(() => {
    const fetchChallenges = async () => {
      if (!user) return;
      setLoading(true);

      try {
        const challengesRef = collection(db, `users/${user.uid}/challenges`);
        const snapshot = await getDocs(challengesRef);
        const dates = getWeekDates();
        setWeekDates(dates);

        // Aggregate weekly data across all challenges
        const weekProgress = {};
        let combinedGoal = 0;

        const data = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const challengeData = doc.data();
            combinedGoal += challengeData.goal || 0;

            // Get progress for this challenge
            const progressRef = collection(db, `users/${user.uid}/challenges/${doc.id}/progress`);
            const progressSnapshot = await getDocs(progressRef);

            // Per-challenge weekly data
            const challengeWeekData = {};

            progressSnapshot.docs.forEach((progressDoc) => {
              const dateStr = progressDoc.id;
              const dayIndex = dates.indexOf(dateStr);
              if (dayIndex !== -1) {
                if (!weekProgress[dayIndex]) {
                  weekProgress[dayIndex] = { total: 0 };
                }
                weekProgress[dayIndex].total += progressDoc.data().total || 0;
                challengeWeekData[dayIndex] = progressDoc.data().total || 0;
              }
            });

            const count = await ChallengeEntry.getChallengeEntryCount(user.uid, doc.id);
            return {
              id: doc.id,
              count: count[0],
              today: count[1],
              weekData: challengeWeekData,
              ...challengeData,
            };
          })
        );

        setChallenges(data);
        setWeekData(weekProgress);
        setTotalGoal(combinedGoal);

        // Load workouts
        const workoutList = await Workout.getAll(user.uid);
        setWorkouts(workoutList);
        const wStats = {};
        await Promise.all(
          workoutList.map(async (workout) => {
            wStats[workout.id] = await WorkoutSession.getStats(user.uid, workout.id);
          })
        );
        setWorkoutStats(wStats);

        // Load active goals
        const goalList = await Goal.getActive(user.uid);
        setGoals(goalList);
        const gStats = {};
        await Promise.all(
          goalList.map(async (goal) => {
            gStats[goal.id] = await GoalAttempt.getStats(user.uid, goal.id, goal);
          })
        );
        setGoalStats(gStats);
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, [user]);

  const calculateExpectedProgress = (count, todayCount, startAt, goal) => {
    const challengeStartDate = startAt.toDate();
    const today = new Date();
    const currentYear = today.getFullYear();
    const yearStart = new Date(currentYear, 0, 1);

    const effectiveStartDate = challengeStartDate < yearStart ? yearStart : challengeStartDate;

    const daysElapsed = Math.ceil((today - effectiveStartDate) / (1000 * 60 * 60 * 24));
    const expectedProgress = daysElapsed * goal;

    const percentage = todayCount > 0 ? (todayCount / goal) * 100 : 0;

    return {
      expectedProgress,
      percentage,
    };
  };

  const getFirstName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0];
    }
    return 'there';
  };

  const formatDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
      {/* Greeting Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 sm:p-6 mb-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="w-14 h-14 rounded-full border-2 border-white/30"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                {getFirstName().charAt(0)}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">Hello, {getFirstName()}</h1>
              <p className="text-white/80 text-sm">{formatDate()}</p>
            </div>
          </div>
        </div>

        {/* Weekly Tracker */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-2 sm:p-4">
          <h3 className="text-sm font-medium text-white/80 mb-3">This Week's Progress</h3>
          {loading ? (
            <div className="flex justify-between gap-2">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="w-8 h-3 bg-white/20 rounded animate-pulse" />
                  <div className="w-12 h-12 bg-white/20 rounded-full animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <WeeklyTracker
              weekData={weekData}
              weekDates={weekDates}
              totalGoal={totalGoal}
              onDayClick={(date) => navigate(`/statistics?date=${date}`)}
            />
          )}
        </div>
      </div>

      {/* Challenges Section */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          My Challenges
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Track your daily progress and stay on target
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : challenges.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No challenges yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first challenge to start tracking your progress
          </p>
          <button
            onClick={() => navigate('/add-challenge')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Create Challenge
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {challenges.map((challenge) => {
            const progress = calculateExpectedProgress(
              challenge.count,
              challenge.today,
              challenge.startAt,
              challenge.goal
            );
            const difference = challenge.count - progress.expectedProgress;
            const isAhead = difference >= 0;

            return (
              <div
                key={challenge.id}
                onClick={() => navigate(`/challenge/${challenge.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
              >
                {challenge.img && (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={challenge.img}
                      alt={challenge.originalName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {challenge.originalName}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        isAhead
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}
                    >
                      {isAhead ? '+' : ''}{difference} {challenge.goalType === 'minutes' ? 'min' : ''}
                    </span>
                  </div>

                  <div className="h-10 mb-4">
                    {challenge.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {challenge.description}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Today's progress</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {challenge.today}/{challenge.goal}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all ${
                          progress.percentage >= 100
                            ? 'bg-green-500'
                            : 'bg-indigo-600'
                        }`}
                        style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Mini Week Tracker */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span>This week</span>
                    </div>
                    <div className="flex justify-between gap-1">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => {
                        const dayTotal = challenge.weekData?.[index] || 0;
                        const hasProgress = dayTotal > 0;
                        const dayPercentage = challenge.goal > 0 ? Math.min((dayTotal / challenge.goal) * 100, 100) : 0;
                        const isToday = index === (new Date().getDay() + 6) % 7;

                        return (
                          <div key={index} className="flex flex-col items-center flex-1">
                            <span className={`text-[10px] mb-1 ${isToday ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
                              {day}
                            </span>
                            <div className={`w-full h-1.5 rounded-full ${hasProgress ? (dayPercentage >= 100 ? 'bg-green-500' : 'bg-red-400') : 'bg-gray-200 dark:bg-gray-700'}`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <span>Total: {challenge.count}</span>
                    <span>Goal: {progress.expectedProgress}</span>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNewEntry();
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    Add Entry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Workouts Section */}
      {!loading && workouts.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                My Workouts
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Track your recurring workout performance
              </p>
            </div>
            <button
              onClick={() => navigate('/workouts')}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium flex items-center gap-1"
            >
              View All
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workouts.slice(0, 3).map((workout) => {
              const stats = workoutStats[workout.id] || {};
              const lastPerformed = workout.lastPerformedAt
                ? (() => {
                    const date = workout.lastPerformedAt.toDate ? workout.lastPerformedAt.toDate() : new Date(workout.lastPerformedAt);
                    const now = new Date();
                    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
                    if (diffDays === 0) return "Today";
                    if (diffDays === 1) return "Yesterday";
                    if (diffDays < 7) return `${diffDays}d ago`;
                    return `${Math.floor(diffDays / 7)}w ago`;
                  })()
                : "Never";

              return (
                <div
                  key={workout.id}
                  onClick={() => navigate(`/workout/${workout.id}`)}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    {workout.imgThumb ? (
                      <img src={workout.imgThumb} alt={workout.name} className="w-12 h-12 object-cover rounded-lg" />
                    ) : (
                      <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">{workout.name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span>Best: {stats.personalBest !== null ? `${stats.personalBest} ${workout.measurementUnit}` : '-'}</span>
                        <span>Last: {lastPerformed}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Goals Section */}
      {!loading && goals.length > 0 && (
        <div className="mt-10 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Active Goals
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Track progress toward your targets
              </p>
            </div>
            <button
              onClick={() => navigate('/goals')}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium flex items-center gap-1"
            >
              View All
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
            {goals.slice(0, 3).map((goal, index) => {
              const stats = goalStats[goal.id] || {};
              const currentValue = stats.currentValue ?? goal.startingValue ?? 0;
              const progress = Goal.calculateProgress(goal, currentValue);
              const daysRemaining = Goal.getDaysRemaining(goal);
              const paceStatus = Goal.getPaceStatus(goal, currentValue);

              return (
                <div
                  key={goal.id}
                  onClick={() => navigate(`/goal/${goal.id}`)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    index < goals.slice(0, 3).length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {goal.imgThumb ? (
                      <img src={goal.imgThumb} alt={goal.title} className="w-10 h-10 object-cover rounded-lg" />
                    ) : (
                      <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{goal.title}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${paceStatus.bgColor} ${paceStatus.color}`}>
                          {paceStatus.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-indigo-600 h-2 rounded-full"
                            style={{ width: `${Math.min(100, progress)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {currentValue}/{goal.targetValue} {goal.targetUnit}
                        </span>
                        <span className="text-sm text-gray-400 dark:text-gray-500 whitespace-nowrap">
                          {daysRemaining}d
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StartPage;
